import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Home as HomeIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Star as StarIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
  SportsEsports as LeagueIcon,
  EmojiEvents as TrophyIcon,
  Schedule as ScheduleIcon,
  SportsEsports as SportsEsportsIcon,
  Build as BuildIcon,
  RocketLaunch as RocketLaunchIcon,
} from '@mui/icons-material';
import { householdService } from '../services/householdService';
import { userService } from '../services/userService';
import { roleService } from '../services/roleService';
import { roleApplicationService } from '../services/roleApplicationService';
import { groupService } from '../services/groupService';
import { groupTypeService } from '../services/groupTypeService';
import AvailabilityManager from './AvailabilityManager';
import AvailabilityEditForm from './AvailabilityEditForm';
import TeamSupervisorAvailabilityForm from './TeamSupervisorAvailabilityForm';
import { useSentryTracking } from './SentryComponentWrapper';
import { 
  trackUIInteraction, 
  trackAPICall, 
  addBreadcrumb, 
  logger, 
  setContext,
  setTag,
  trackError 
} from '../utils/sentryUtils';
import { availabilityService } from '../services/availabilityService';

const HouseholdDashboard = ({ authData }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const householdId = authData?.householdId;
  
  // Initialize Sentry tracking for this component
  useSentryTracking('HouseholdDashboard', { includeUpdates: true, includeRender: true });
  
  const [household, setHousehold] = useState(null);
  const [caregivers, setCaregivers] = useState([]);
  const [students, setStudents] = useState([]);
  const [primaryCaregiver, setPrimaryCaregiver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [reassignPrimaryDialogOpen, setReassignPrimaryDialogOpen] = useState(false);
  const [teamSupervisorDialogOpen, setTeamSupervisorDialogOpen] = useState(false);
  const [ftcLeagueDialogOpen, setFtcLeagueDialogOpen] = useState(false);
  const [caregiverAvailabilityDialogOpen, setCaregiverAvailabilityDialogOpen] = useState(false);
  const [selectedCaregiverForAvailability, setSelectedCaregiverForAvailability] = useState('');
  const [newMemberData, setNewMemberData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    role: 'Student', // Default to Student
  });
  const [selectedPrimaryCaregiver, setSelectedPrimaryCaregiver] = useState('');
  const [teamSupervisorNotes, setTeamSupervisorNotes] = useState('');
  const [selectedCaregiverForApplication, setSelectedCaregiverForApplication] = useState('');
  const [hasPendingTeamSupervisorApplication, setHasPendingTeamSupervisorApplication] = useState(false);
  const [pendingApplicationsByCaregiver, setPendingApplicationsByCaregiver] = useState({});
  const [roleApplications, setRoleApplications] = useState([]);
  const [teamSupervisorAvailabilities, setTeamSupervisorAvailabilities] = useState([]);
  
  // FTC League enrollment states
  const [availableFtcLeagues, setAvailableFtcLeagues] = useState([]);
  const [allLeagues, setAllLeagues] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedLeague, setSelectedLeague] = useState('');
  const [setSeekingTeam, setSetSeekingTeam] = useState(true);
  const [groupTypes, setGroupTypes] = useState([]);
  const [newlyAddedStudentId, setNewlyAddedStudentId] = useState(null);
  
  // Availability edit states
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [selectedStudentForAvailability, setSelectedStudentForAvailability] = useState('');

  // Helper function to check if current user is the primary caregiver
  const isCurrentUserPrimaryCaregiver = () => {
    return primaryCaregiver && authData?.userId && primaryCaregiver.id === authData.userId;
  };



  // Check for pending applications
  const checkPendingApplications = async () => {
    try {
      // Get all pending applications for the household
      const pendingApplications = await roleApplicationService.getApplicationsByHousehold(householdId);
      
      // Filter for team supervisor applications and group by caregiver
      const teamSupervisorApps = pendingApplications.filter(app => 
        app.roleName === 'Team Supervisor' && app.status === 'PENDING'
      );
      
      const appsByCaregiver = {};
      let hasAnyPending = false;
      
      teamSupervisorApps.forEach(app => {
        if (app.caregiverId) {
          appsByCaregiver[app.caregiverId] = true;
          hasAnyPending = true;
        }
      });
      
      setPendingApplicationsByCaregiver(appsByCaregiver);
      setHasPendingTeamSupervisorApplication(hasAnyPending);
      
    } catch (err) {
      console.error('Error checking pending applications:', err);
    }
  };

  // Load available FTC leagues
  const loadAvailableFtcLeagues = async () => {
    try {
      const allGroups = await groupService.getAllGroups();
      
      // Filter for groups with type "League-Students"
      const ftcLeagues = allGroups.filter(group => 
        group.groupTypeName && group.groupTypeName === 'League-Students'
      );
      setAvailableFtcLeagues(ftcLeagues);
      
      // Also set all leagues for enrollment checking
      const leagues = allGroups.filter(group => 
        group.groupTypeName && group.groupTypeName.startsWith('League-')
      );
      setAllLeagues(leagues);
    } catch (err) {
      console.error('Error loading FTC leagues:', err);
      setError('Failed to load available FTC leagues');
    }
  };

  // Load group types
  const loadGroupTypes = async () => {
    try {
      const types = await groupTypeService.getAllGroupTypes();
      setGroupTypes(types);
    } catch (err) {
      console.error('Error loading group types:', err);
    }
  };

  // Handle FTC league enrollment
  const handleFtcLeagueEnrollment = async () => {
    try {
      setError('');
      if (!selectedStudent || !selectedLeague) {
        setError('Please select both a student and a league');
        return;
      }

      // TEMPORARY: Check if the selected student is already enrolled in any league
      console.log('Household data for validation:', household);
      const selectedStudentUser = household?.users?.find(user => user.id === selectedStudent);
      const studentEnrolledLeagues = [];
      
      if (selectedStudentUser?.groupMemberships) {
        selectedStudentUser.groupMemberships.forEach(membership => {
          if (membership.groupTypeName?.startsWith('League-')) {
            studentEnrolledLeagues.push(membership.groupName);
          }
        });
      }
      
      console.log('Leagues this student is enrolled in:', studentEnrolledLeagues);
      
      if (studentEnrolledLeagues.length > 0) {
        const leagueNames = studentEnrolledLeagues.join(', ');
        setError(`Student ${formatUserName(selectedStudentUser)} is already enrolled in league(s): ${leagueNames}. Temporarily, students can only be enrolled in one league at a time.`);
        return;
      }

      // Get the current user for the createdBy parameter
      const currentUser = { id: authData.userId }; // Simplified user object
      
      // Add the student to the selected league
      const leagueToUpdate = availableFtcLeagues.find(league => league.id === selectedLeague);
      if (!leagueToUpdate) {
        setError('Selected league not found');
        return;
      }

      // Get current members and add the selected student
      const currentUserIds = leagueToUpdate.userIds || [];
      const updatedUserIds = [...currentUserIds, selectedStudent];

      const updateData = {
        ...leagueToUpdate,
        userIds: updatedUserIds
      };

      await groupService.updateGroup(selectedLeague, updateData, currentUser.id);
      
      // If checkbox is checked, also set the student as seeking team
      if (setSeekingTeam) {
        try {
          const selectedStudentUser = household?.users?.find(user => user.id === selectedStudent);
          if (selectedStudentUser) {
            await userService.updateTeamSeekingStatus(selectedStudent, true);
          }
        } catch (err) {
          console.error('Failed to update seeking team status:', err);
          // Don't fail the entire operation if this fails
        }
      }
      
      setSuccess('Student successfully enrolled in FTC league!' + (setSeekingTeam ? ' and marked as seeking team.' : '') + ' Now opening availability dialog to set their schedule.');
      setFtcLeagueDialogOpen(false);
      
      // Store the enrolled student ID to open availability dialog
      const enrolledStudentId = selectedStudent;
      
      // Clear form fields
      setSelectedStudent('');
      setSelectedLeague('');
      setSetSeekingTeam(false);
      
      // Reload household data to reflect changes
      await loadHouseholdData();
      
      // Open availability dialog for the enrolled student
      if (enrolledStudentId) {
        setSelectedStudentForAvailability(enrolledStudentId);
        setAvailabilityDialogOpen(true);
      }
    } catch (err) {
      setError('Failed to enroll student in FTC league: ' + err.message);
    }
  };

  // Handle caregiver availability changes
  const handleCaregiverAvailabilityChange = async (availabilities) => {
    try {
      setError('');
      
      // Track the availability change interaction
      trackUIInteraction('caregiver_availability_change', {
        caregiverId: selectedCaregiverForAvailability,
        householdId,
        availabilitiesCount: availabilities.length
      });
      
      addBreadcrumb("Caregiver availability changed", "ui.interaction", {
        caregiverId: selectedCaregiverForAvailability,
        householdId,
        availabilitiesCount: availabilities.length
      });
      
      // Get the selected caregiver's user ID
      const caregiverUser = caregivers.find(c => c.id === selectedCaregiverForAvailability);
      if (!caregiverUser) {
        setError('Selected caregiver not found');
        return;
      }

      // Track the API call
      trackAPICall('PUT', `/user-availability/user/${caregiverUser.id}`, () => {
        // This is the callback that will be executed within the Sentry span
        // The actual API call is already done above, so this is just for tracking
      }, {
        caregiverId: caregiverUser.id,
        caregiverName: formatUserName(caregiverUser),
        availabilitySlots: availabilities.length
      });

      // Save the availabilities using the standard availability service
      // This endpoint expects UserAvailabilityUpdateDto format which is more robust
      const availabilityUpdateDto = {
        slots: availabilities.map(avail => ({
          locationId: parseInt(avail.locationId),
          dayOfWeek: avail.dayOfWeek,
          startTime: avail.startTime,
          endTime: avail.endTime,
          isAvailable: avail.isAvailable
        }))
      };
      
      await availabilityService.updateUserAvailability(caregiverUser.id, availabilityUpdateDto);
      
      setSuccess('Caregiver availability updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      
      // Close the dialog and reset state
      setCaregiverAvailabilityDialogOpen(false);
      setSelectedCaregiverForAvailability('');
      
      // Track successful update
      trackUIInteraction('caregiver_availability_updated', () => {
        // This is the callback that will be executed within the Sentry span
        // The actual work is already done above, so this is just for tracking
      }, {
        caregiverId: caregiverUser.id,
        caregiverName: formatUserName(caregiverUser),
        availabilitySlots: availabilities.length
      });
      
      logger.info("Caregiver availability updated successfully", {
        caregiverId: caregiverUser.id,
        caregiverName: formatUserName(caregiverUser),
        householdId,
        availabilitySlots: availabilities.length
      });
      
    } catch (err) {
      console.error('Error updating caregiver availability:', err);
      setError('Failed to update caregiver availability: ' + err.message);
      
      // Track error in Sentry
      trackError(err, {
        context: 'Caregiver Availability Update',
        caregiverId: selectedCaregiverForAvailability,
        availabilitySlots: availabilities.length
      });
    }
  };

  // Handle team supervisor application submission
  const handleTeamSupervisorApplication = async () => {
    try {
      setError('');
      
      // Track the application submission
      trackUIInteraction('team_supervisor_application_submit', {
        userId: authData.userId,
        householdId,
        hasNotes: !!teamSupervisorNotes.trim(),
        availabilitiesCount: teamSupervisorAvailabilities.length,
        selectedCaregiver: selectedCaregiverForApplication
      });
      
      addBreadcrumb("Team supervisor application submitted", "ui.interaction", {
        userId: authData.userId,
        householdId,
        hasNotes: !!teamSupervisorNotes.trim(),
        availabilitiesCount: teamSupervisorAvailabilities.length
      });
      
      const roles = await roleService.getAllRoles();
      const teamSupervisorRole = roles.find(role => role.name === 'Team Supervisor');
      
      if (!teamSupervisorRole) {
        throw new Error('Team Supervisor role not found');
      }
      
      // Submit the application
      const application = await roleApplicationService.submitTeamSupervisorApplication(
        authData.userId,
        teamSupervisorRole.id,
        householdId,
        teamSupervisorNotes,
        selectedCaregiverForApplication || null
      );
      
      // Save the team supervisor availabilities
      if (teamSupervisorAvailabilities.length > 0) {
        try {
          await availabilityService.saveTeamSupervisorAvailabilities(
            authData.userId,
            teamSupervisorAvailabilities
          );
          console.log('Team supervisor availabilities saved successfully');
          
          logger.info("Team supervisor availabilities saved successfully", {
            userId: authData.userId,
            availabilitiesCount: teamSupervisorAvailabilities.length,
            householdId
          });
        } catch (err) {
          console.warn('Failed to save team supervisor availabilities:', err);
          trackError(err, "Failed to save team supervisor availabilities", {
            userId: authData.userId,
            availabilitiesCount: teamSupervisorAvailabilities.length,
            householdId
          });
          // Don't fail the entire application if this fails
        }
      }
      
      setSuccess('Team Supervisor application submitted successfully!');
      setTeamSupervisorDialogOpen(false);
      setTeamSupervisorNotes('');
      setSelectedCaregiverForApplication('');
      setTeamSupervisorAvailabilities([]);
      
      // Refresh pending applications to update the UI
      await checkPendingApplications();
      
      logger.info("Team supervisor application submitted successfully", {
        userId: authData.userId,
        householdId,
        applicationId: application.id,
        availabilitiesCount: teamSupervisorAvailabilities.length
      });
      
    } catch (err) {
      setError('Failed to submit Team Supervisor application');
      console.error('Error submitting team supervisor application:', err);
      
      trackError(err, "Failed to submit team supervisor application", {
        userId: authData.userId,
        householdId,
        hasNotes: !!teamSupervisorNotes.trim(),
        availabilitiesCount: teamSupervisorAvailabilities.length
      });
    }
  };

  // Handle team supervisor availability changes
  const handleTeamSupervisorAvailabilitiesChange = (availabilities) => {
    // Track availability changes
    trackUIInteraction('team_supervisor_availabilities_change', {
      userId: authData.userId,
      householdId,
      availabilitiesCount: availabilities.length,
      previousCount: teamSupervisorAvailabilities.length
    });
    
    addBreadcrumb("Team supervisor availabilities updated", "ui.interaction", {
      userId: authData.userId,
      householdId,
      availabilitiesCount: availabilities.length
    });
    
    logger.info("Team supervisor availabilities updated", {
      userId: authData.userId,
      householdId,
      availabilitiesCount: availabilities.length,
      previousCount: teamSupervisorAvailabilities.length
    });
    
    setTeamSupervisorAvailabilities(availabilities);
  };

  useEffect(() => {
    if (householdId) {
      // Set Sentry context for household dashboard session
      setContext("household_dashboard", {
        householdId: householdId,
        userId: authData?.userId,
        userEmail: authData?.userEmail,
        userName: authData?.userName,
        accessTime: new Date().toISOString()
      });
      
      setTag("household.dashboard_loaded", "true");
      setTag("household.user_type", "household_member");
      
      addBreadcrumb("Household dashboard initialized", "ui.component", {
        householdId: householdId,
        userId: authData?.userId,
        userEmail: authData?.userEmail
      });
      
      logger.info("Household dashboard initialized", {
        householdId: householdId,
        userId: authData?.userId,
        userEmail: authData?.userEmail,
        userName: authData?.userName
      });
      
      loadHouseholdData();
      checkPendingApplications();
      loadRoleApplications();
      loadAvailableFtcLeagues();
      loadGroupTypes();
    } else {
      setLoading(false);
    }
  }, [householdId, authData]);

  // Check pending applications when caregiver selection changes
  useEffect(() => {
    if (selectedCaregiverForApplication) {
      checkPendingApplications();
    }
  }, [selectedCaregiverForApplication]);



  // Check for URL parameters to open league enrollment dialog or availability dialog
  useEffect(() => {
    const openLeagueEnrollment = searchParams.get('openLeagueEnrollment');
    const openAvailability = searchParams.get('openAvailability');
    const studentIdParam = searchParams.get('studentId');
    
    if (openLeagueEnrollment === 'true' && !loading && students.length > 0 && availableFtcLeagues.length > 0) {
      setFtcLeagueDialogOpen(true);
      
      // Auto-select the specific student that was just added
      if (studentIdParam) {
        const targetStudent = students.find(student => student.id.toString() === studentIdParam);
        if (targetStudent) {
          setSelectedStudent(targetStudent.id);
          setNewlyAddedStudentId(targetStudent.id);
        }
      } else if (students.length === 1) {
        // Fallback: if no specific student ID, select the only student
        setSelectedStudent(students[0].id);
        setNewlyAddedStudentId(students[0].id);
      }
      
      // Clear the parameters from the URL
      navigate('/household/dashboard', { replace: true });
    } else if (openAvailability === 'true' && !loading && students.length > 0 && studentIdParam) {
      // Open availability dialog for the specified student
      const targetStudent = students.find(student => student.id.toString() === studentIdParam);
      if (targetStudent) {
        setSelectedStudentForAvailability(targetStudent.id);
        setAvailabilityDialogOpen(true);
      }
      
      // Clear the parameters from the URL
      navigate('/household/dashboard', { replace: true });
    }
  }, [searchParams, loading, students.length, availableFtcLeagues.length, allLeagues.length, navigate]);

  // Auto-select student when dialog opens and there's only one student
  useEffect(() => {
    if (ftcLeagueDialogOpen && students.length === 1 && !selectedStudent) {
      setSelectedStudent(students[0].id);
    }
  }, [ftcLeagueDialogOpen, students.length, selectedStudent, allLeagues.length]);

  // Track newly added students and auto-select them when the dialog opens
  useEffect(() => {
    if (ftcLeagueDialogOpen && newlyAddedStudentId && !selectedStudent) {
      setSelectedStudent(newlyAddedStudentId);
    }
  }, [ftcLeagueDialogOpen, newlyAddedStudentId, selectedStudent]);

  const loadHouseholdData = async () => {
    return trackAPICall(
      "GET",
      `/api/households/${householdId}/detail`,
      async () => {
        try {
          setLoading(true);
          setError('');
          
          // Set Sentry context for household data loading
          setContext("household_data", {
            householdId: householdId,
            userId: authData?.userId,
            userEmail: authData?.userEmail
          });
          
          setTag("household.id", householdId.toString());
          setTag("household.data_loading", "true");
          
          addBreadcrumb("Loading household data", "api", {
            householdId: householdId,
            userId: authData?.userId
          });
          
          // Load household details
          const householdData = await householdService.getHouseholdDetailById(householdId);
          setHousehold(householdData);
          
          // Separate users by role
          const allUsers = householdData.users || [];
          
          const caregiverUsers = allUsers.filter(user => 
            user.roleNames && user.roleNames.includes('Caregiver')
          );
          const studentUsers = allUsers.filter(user => 
            user.roleNames && user.roleNames.includes('Student')
          );
          
          setCaregivers(caregiverUsers);
          setStudents(studentUsers);
          
          // Set primary caregiver from household data
          if (householdData.primaryCaregiverId) {
            const primary = caregiverUsers.find(c => c.id === householdData.primaryCaregiverId);
            if (primary) {
              setPrimaryCaregiver(primary);
            } else if (caregiverUsers.length > 0) {
              // Fallback to first caregiver if primary not found
              setPrimaryCaregiver(caregiverUsers[0]);
            }
          } else if (caregiverUsers.length > 0) {
            // No primary caregiver set, use first caregiver
            setPrimaryCaregiver(caregiverUsers[0]);
          }
          
          logger.info("Household data loaded successfully", {
            householdId: householdId,
            userId: authData?.userId,
            caregiverCount: caregiverUsers.length,
            studentCount: studentUsers.length,
            hasPrimaryCaregiver: !!householdData.primaryCaregiverId
          });
          
          return householdData;
          
        } catch (err) {
          trackError(err, {
            component: "HouseholdDashboard",
            action: "load_household_data",
            householdId: householdId,
            userId: authData?.userId
          });
          
          logger.error("Failed to load household data", {
            component: "HouseholdDashboard",
            householdId: householdId,
            userId: authData?.userId,
            error: err.message
          });
          
          setError('Failed to load household data');
          throw err;
        } finally {
          setLoading(false);
        }
      },
      {
        householdId: householdId,
        userId: authData?.userId,
        userEmail: authData?.userEmail
      }
    );
  };

  const handleAddMember = async () => {
    return trackUIInteraction(
      "Add Member to Household",
      async () => {
        try {
          setError('');
          
          addBreadcrumb("Adding member to household", "ui.interaction", {
            householdId: householdId,
            userId: authData?.userId,
            memberRole: newMemberData.role
          });
          
          // Get the role ID for the selected role
          const roles = await roleService.getAllRoles();
          const selectedRole = roles.find(role => role.name === newMemberData.role);
          
          if (!selectedRole) {
            throw new Error(`Role ${newMemberData.role} not found`);
          }
          
          // Create the new user
          const userData = {
            firstName: newMemberData.firstName,
            lastName: newMemberData.lastName,
            email: newMemberData.email,
            phoneNumber: newMemberData.phoneNumber,
            roleIds: [selectedRole.id],
            householdIds: [householdId],
            isActive: true,
          };
          
          const createdUser = await userService.createUser(userData);
          
          setSuccess(`${newMemberData.firstName} ${newMemberData.lastName} has been added to the household`);
          setAddMemberDialogOpen(false);
          setNewMemberData({
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            role: 'Student',
          });
          
          // Reload household data
          await loadHouseholdData();
          
          logger.info("Member added to household successfully", {
            householdId: householdId,
            userId: authData?.userId,
            newMemberId: createdUser.id,
            newMemberRole: newMemberData.role,
            newMemberName: `${newMemberData.firstName} ${newMemberData.lastName}`
          });
          
          return createdUser;
          
        } catch (err) {
          trackError(err, {
            component: "HouseholdDashboard",
            action: "add_member",
            householdId: householdId,
            userId: authData?.userId,
            memberRole: newMemberData.role
          });
          
          logger.error("Failed to add member to household", {
            component: "HouseholdDashboard",
            householdId: householdId,
            userId: authData?.userId,
            memberRole: newMemberData.role,
            error: err.message
          });
          
          setError('Failed to add member to household');
          throw err;
        }
      },
      {
        householdId: householdId,
        userId: authData?.userId,
        memberRole: newMemberData.role,
        memberName: `${newMemberData.firstName} ${newMemberData.lastName}`
      }
    );
  };

  const handleReassignPrimaryCaregiver = async () => {
    try {
      setError('');
      
      // Update the primary caregiver in the backend
      await householdService.updatePrimaryCaregiver(householdId, parseInt(selectedPrimaryCaregiver));
      
      // Update local state
      const newPrimary = caregivers.find(c => c.id === parseInt(selectedPrimaryCaregiver));
      if (newPrimary) {
        setPrimaryCaregiver(newPrimary);
        setSuccess('Primary caregiver has been reassigned');
        setReassignPrimaryDialogOpen(false);
        setSelectedPrimaryCaregiver('');
      }
      
    } catch (err) {
      setError('Failed to reassign primary caregiver');
      console.error('Error reassigning primary caregiver:', err);
    }
  };

  const formatAddress = (household) => {
    const parts = [
      household.addressStreet,
      household.addressCity,
      household.addressZip
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'No address provided';
  };

  const formatUserName = (user) => {
    const name = user.preferredName || user.firstName;
    const lastNameInitial = user.lastName ? user.lastName.substring(0, 1) : '';
    const graduationYear = user.graduationYear || '';
    return `${name} ${lastNameInitial}. ${graduationYear}`;
  };

  const getAvailableStudentsForLeagueEnrollment = () => {
    // Filter out students who are already enrolled in any league
    return students.filter(student => {
      // Check if student is enrolled in any league
      const isEnrolledInLeague = allLeagues.some(league => {
        if (!league.userIds || league.userIds.length === 0) return false;
        return league.userIds.includes(student.id);
      });
      
      return !isEnrolledInLeague;
    });
  };

  const getGroupTypeName = (groupTypeId, groupTypeName) => {
    // If groupTypeName is already available in the league data, use it
    if (groupTypeName) {
      return groupTypeName;
    }

    // Handle undefined or null groupTypeId
    if (!groupTypeId) {
      console.error('GroupTypeId is undefined/null:', { groupTypeId });
      return 'Unknown Type';
    }

    // Convert to number for comparison since IDs might be strings
    const numericId = Number(groupTypeId);
    const groupType = groupTypes.find(gt => gt.id === numericId);

    // Debug logging
    if (!groupType) {
      console.error('GroupType not found:', {
        groupTypeId,
        numericId,
        availableGroupTypes: groupTypes.map(gt => ({ id: gt.id, name: gt.name }))
      });
    }

    return groupType ? groupType.name : 'Unknown Type';
  };

  const loadRoleApplications = async () => {
    try {
      const applications = await roleApplicationService.getApplicationsByHousehold(householdId);
      setRoleApplications(applications);
    } catch (err) {
      console.error('Error loading role applications:', err);
    }
  };

  const getApplicationStatusForCaregiver = (caregiverId) => {
    const application = roleApplications.find(app => 
      app.caregiverId === caregiverId && app.roleName === 'Team Supervisor' && app.status === 'PENDING'
    );
    
    if (!application) return null;
    
    return {
      adminApproved: application.adminApproved,
      backgroundCheckReceived: application.backgroundCheckReceived,
      backgroundCheckApproved: application.backgroundCheckApproved,
      ichatReceived: application.ichatReceived,
      ichatApproved: application.ichatApproved
    };
  };

  const renderApplicationStatus = (status) => {
    if (!status) return null;
    
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          Team Supervisor Application Status:
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip 
            label="Admin Approved" 
            color={status.adminApproved ? "success" : "default"} 
            size="small" 
            variant="outlined"
          />
          <Chip 
            label="BC Received" 
            color={status.backgroundCheckReceived ? "success" : "default"} 
            size="small" 
            variant="outlined"
          />
          <Chip 
            label="BC Approved" 
            color={status.backgroundCheckApproved ? "success" : "default"} 
            size="small" 
            variant="outlined"
          />
          {status.ichatReceived && (
            <Chip 
              label="ICHAT Received" 
              color={status.ichatReceived ? "success" : "default"} 
              size="small" 
              variant="outlined"
            />
          )}
          {status.ichatApproved && (
            <Chip 
              label="ICHAT Approved" 
              color={status.ichatApproved ? "success" : "default"} 
              size="small" 
              variant="outlined"
            />
          )}
        </Box>
      </Box>
    );
  };

  // Check if the selected caregiver has a pending application
  const hasSelectedCaregiverPendingApplication = () => {
    if (!selectedCaregiverForApplication) return false;
    return pendingApplicationsByCaregiver[selectedCaregiverForApplication] || false;
  };

  // Get available caregivers for team supervisor application (those without pending applications)
  const getAvailableCaregiversForApplication = () => {
    return caregivers.filter(caregiver => !pendingApplicationsByCaregiver[caregiver.id]);
  };

  // Check if there are any caregivers available to apply for
  const hasAvailableCaregivers = () => {
    return getAvailableCaregiversForApplication().length > 0;
  };

  if (!authData || !householdId) {
    return (
      <Alert severity="warning">
        Authentication data not available. Please log in again.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!household) {
    return (
      <Alert severity="warning">
        Household not found
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {household.name} Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Household Code: <strong>{household.householdCode}</strong>
          </Typography>
        </Box>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Quick Actions */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Grid container spacing={2}>
            <Grid xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<PersonAddIcon />}
                onClick={() => {
                  // Navigate to student sign-up form with household code pre-filled
                  const householdCode = household?.householdCode || '';
                  navigate(`/signup/student?householdCode=${householdCode}&mode=join`);
                }}
              >
                Add Student
              </Button>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<LeagueIcon />}
                onClick={() => setFtcLeagueDialogOpen(true)}
                disabled={students.length === 0 || availableFtcLeagues.length === 0}
              >
                Enroll in FTC League
              </Button>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<ScheduleIcon />}
                onClick={() => setAvailabilityDialogOpen(true)}
                disabled={students.length === 0}
              >
                Edit Student Availability
              </Button>
            </Grid>
            <Grid xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<PersonAddIcon />}
                onClick={() => {
                  // Navigate to caregiver sign-up form with household code pre-filled
                  const householdCode = household?.householdCode || '';
                  navigate(`/signup/caregiver?householdCode=${householdCode}&mode=join`);
                }}
              >
                Add Caregiver
              </Button>
            </Grid>
            {isCurrentUserPrimaryCaregiver() && (
              <Grid xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<EditIcon />}
                  onClick={() => setReassignPrimaryDialogOpen(true)}
                  disabled={caregivers.length === 0}
                >
                  Reassign Primary Caregiver
                </Button>
              </Grid>
            )}
            <Grid xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<PersonAddIcon />}
                onClick={() => {
                  setTeamSupervisorDialogOpen(true);
                }}
                disabled={!hasAvailableCaregivers()}
              >
                {!hasAvailableCaregivers() 
                  ? 'All Caregivers Have Pending Applications'
                  : 'Apply for Team Supervisor'
                }
              </Button>
            </Grid>
            {caregivers.length > 0 && (
              <Grid xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ScheduleIcon />}
                  onClick={() => {
                    // Track the caregiver availability modification action
                    trackUIInteraction('caregiver_availability_modify_clicked', () => {
                      // This is the callback that will be executed within the Sentry span
                      setCaregiverAvailabilityDialogOpen(true);
                    }, {
                      householdId: householdId,
                      caregiversCount: caregivers.length
                    });
                  }}
                >
                  Modify Caregiver Availability
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Household Information */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HomeIcon />
                Household Information
              </Typography>
              <Grid container spacing={3}>
                <Grid xs={12} md={6}>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Name:</strong> {household.name}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Address:</strong> {formatAddress(household)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Created:</strong> {new Date(household.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Grid>
                <Grid xs={12} md={6}>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StarIcon fontSize="small" />
                      <strong>Primary Caregiver:</strong>
                    </Typography>
                    {primaryCaregiver ? (
                      <Box sx={{ ml: 3 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>{formatUserName(primaryCaregiver)}</strong>
                        </Typography>
                        {primaryCaregiver.email && (
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmailIcon fontSize="small" />
                            {primaryCaregiver.email}
                          </Typography>
                        )}
                        {primaryCaregiver.phoneNumber && (
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon fontSize="small" />
                            {primaryCaregiver.phoneNumber}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
                        No primary caregiver assigned
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Members Lists */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Caregivers */}
        <Grid xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon />
                  Caregivers ({caregivers.length})
                </Typography>
              </Box>
              {caregivers.length > 0 ? (
                <List dense>
                  {caregivers.map((caregiver) => (
                    <ListItem key={caregiver.id} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {primaryCaregiver && primaryCaregiver.id === caregiver.id ? (
                          <StarIcon color="primary" />
                        ) : (
                          <PeopleIcon color="action" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={formatUserName(caregiver)}
                        secondary={
                          <Box>
                            {caregiver.email && (
                              <Typography variant="caption" display="block">
                                {caregiver.email}
                              </Typography>
                            )}
                            {caregiver.phoneNumber && (
                              <Typography variant="caption" display="block">
                                {caregiver.phoneNumber}
                              </Typography>
                            )}
                            {renderApplicationStatus(getApplicationStatusForCaregiver(caregiver.id))}
                          </Box>
                        }
                      />
                      {primaryCaregiver && primaryCaregiver.id === caregiver.id && (
                        <Chip label="Primary" color="primary" size="small" />
                      )}
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No caregivers in this household
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Students */}
        <Grid xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchoolIcon />
                  Students ({students.length})
                </Typography>
              </Box>
              {students.length > 0 ? (
                <Box>
                  {students.map((student) => (
                    <Paper key={student.id} elevation={0} sx={{ p: 2, mb: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1}>
                          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                            {formatUserName(student)}
                          </Typography>
                          
                          {/* School Info */}
                          {(student.school || student.email) && (
                            <Box sx={{ mb: 1 }}>
                              {student.school && (
                                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <SchoolIcon fontSize="small" />
                                  {student.school}
                                </Typography>
                              )}
                              {student.email && (
                                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <EmailIcon fontSize="small" />
                                  {student.email}
                                </Typography>
                              )}
                            </Box>
                          )}
                          
                          {/* League Memberships */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <TrophyIcon fontSize="small" />
                              League Memberships:
                            </Typography>
                            {student.groupMemberships && student.groupMemberships.length > 0 ? (
                              (() => {
                                const leagueMemberships = student.groupMemberships.filter(
                                  membership => membership.groupTypeName === 'League-Students'
                                );
                                if (leagueMemberships.length > 0) {
                                  return (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {leagueMemberships.map((membership) => (
                                        <Chip
                                          key={membership.groupId}
                                          label={membership.groupName}
                                          color="primary"
                                          size="small"
                                          variant="outlined"
                                        />
                                      ))}
                                    </Box>
                                  );
                                } else {
                                  return (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                      Not enrolled in any leagues
                                    </Typography>
                                  );
                                }
                              })()
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Not enrolled in any leagues
                              </Typography>
                            )}
                          </Box>
                          
                          {/* Availability Manager */}
                          <AvailabilityManager 
                            student={student} 
                          />
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No students in this household
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Member Dialog */}
      <Dialog open={addMemberDialogOpen} onClose={() => setAddMemberDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Member</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={newMemberData.firstName}
                onChange={(e) => setNewMemberData({ ...newMemberData, firstName: e.target.value })}
                required
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={newMemberData.lastName}
                onChange={(e) => setNewMemberData({ ...newMemberData, lastName: e.target.value })}
                required
              />
            </Grid>
            <Grid xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={newMemberData.email}
                onChange={(e) => setNewMemberData({ ...newMemberData, email: e.target.value })}
              />
            </Grid>
            <Grid xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                value={newMemberData.phoneNumber}
                onChange={(e) => setNewMemberData({ ...newMemberData, phoneNumber: e.target.value })}
              />
            </Grid>
            <Grid xs={12}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={newMemberData.role}
                  onChange={(e) => setNewMemberData({ ...newMemberData, role: e.target.value })}
                  label="Role"
                >
                  <MenuItem value="Caregiver">Caregiver</MenuItem>
                  <MenuItem value="Student">Student</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMemberDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddMember}
            variant="contained"
            disabled={!newMemberData.firstName || !newMemberData.lastName}
          >
            Add Member
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reassign Primary Caregiver Dialog */}
      <Dialog open={reassignPrimaryDialogOpen} onClose={() => setReassignPrimaryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reassign Primary Caregiver</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a new primary caregiver for this household:
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Primary Caregiver</InputLabel>
            <Select
              value={selectedPrimaryCaregiver}
              onChange={(e) => setSelectedPrimaryCaregiver(e.target.value)}
              label="Primary Caregiver"
            >
              {caregivers.map((caregiver) => (
                <MenuItem key={caregiver.id} value={caregiver.id}>
                  {formatUserName(caregiver)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReassignPrimaryDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleReassignPrimaryCaregiver}
            variant="contained"
            disabled={!selectedPrimaryCaregiver}
          >
            Reassign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Team Supervisor Application Dialog */}
      <Dialog open={teamSupervisorDialogOpen} onClose={() => setTeamSupervisorDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Apply for Team Supervisor</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide information for your Team Supervisor application. This will be reviewed by an administrator.
          </Typography>
          
          {/* Caregiver Selection */}
          {!hasAvailableCaregivers() ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              All caregivers in your household already have pending Team Supervisor applications.
            </Alert>
          ) : (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Select Caregiver *</InputLabel>
              <Select
                value={selectedCaregiverForApplication}
                onChange={(e) => setSelectedCaregiverForApplication(e.target.value)}
                label="Select Caregiver *"
                required
              >
                <MenuItem value="">
                  <em>Select a caregiver from your household</em>
                </MenuItem>
                {getAvailableCaregiversForApplication().map((caregiver) => (
                  <MenuItem key={caregiver.id} value={caregiver.id}>
                    {formatUserName(caregiver)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Information Note */}
          <Alert severity="info" sx={{ mb: 3 }}>
            Please note preference for which child you'd like to be a team supervisor with, as well as a preference for team number, or any other information you think is helpful to admin.
          </Alert>

          {/* Notes Field */}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Notes"
            value={teamSupervisorNotes}
            onChange={(e) => setTeamSupervisorNotes(e.target.value)}
            placeholder="Please note preference for which child you'd like to be a team supervisor with, as well as a preference for team number, or any other information you think is helpful to admin."
            required
            sx={{ mb: 3 }}
          />

          {/* Team Supervisor Availabilities */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            Team Supervisor Availabilities
          </Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            Please indicate your availability for team supervision. Available locations are based on your students' locations plus GATE (hub location).
            Selecting more options allows us more flexibility in scheduling, you are not signing up to be everywhere at once. You are also not required to go to multiple locations, but the option to be available for multiple locations is there if you can.
            <br /><br />
            <strong>Note:</strong> Your availabilities are automatically saved as you make changes, so no need to worry about losing your selections!
          </Alert>
          
          <TeamSupervisorAvailabilityForm
            householdId={householdId}
            students={students}
            onAvailabilitiesChange={handleTeamSupervisorAvailabilitiesChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setTeamSupervisorDialogOpen(false);
            setSelectedCaregiverForApplication('');
          }}>Cancel</Button>
          <Button 
            onClick={handleTeamSupervisorApplication}
            variant="contained"
            disabled={!teamSupervisorNotes.trim() || !selectedCaregiverForApplication}
          >
            Submit Application
          </Button>
        </DialogActions>
      </Dialog>

      {/* FTC League Enrollment Dialog */}
      <Dialog open={ftcLeagueDialogOpen} onClose={() => setFtcLeagueDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Enroll Student in FTC League</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a student from your household and choose an available FTC league to enroll them.
          </Typography>
          
          {getAvailableStudentsForLeagueEnrollment().length === 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              All students in this household are already enrolled in leagues.
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Select Student</InputLabel>
            <Select
              value={selectedStudent}
              onChange={(e) => setSelectedStudent(e.target.value)}
              label="Select Student"
            >
              {getAvailableStudentsForLeagueEnrollment().map((student) => (
                <MenuItem key={student.id} value={student.id}>
                  {formatUserName(student)}
                  {student.school && ` - ${student.school}`}
                  {student.graduationYear && ` (Class of ${student.graduationYear})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="h6" sx={{ mb: 2 }}>
            Select League
          </Typography>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row', 
            gap: 2, 
            mb: 3,
            width: '100%'
          }}>
            {(() => {
              // Sort leagues by priority: Play, Build, Aspire
              const sortedLeagues = [...availableFtcLeagues].sort((a, b) => {
                const getPriority = (name) => {
                  const lowerName = name.toLowerCase();
                  if (lowerName.includes('play')) return 1;
                  if (lowerName.includes('build')) return 2;
                  if (lowerName.includes('aspire')) return 3;
                  return 4;
                };
                return getPriority(a.name) - getPriority(b.name);
              });

              return sortedLeagues.map((league) => {
                const isSelected = selectedLeague === league.id;
                const getLeagueIcon = (leagueName) => {
                  if (leagueName.toLowerCase().includes('play')) return <SportsEsportsIcon sx={{ fontSize: '6rem' }} />;
                  if (leagueName.toLowerCase().includes('build')) return <BuildIcon sx={{ fontSize: '6rem' }} />;
                  if (leagueName.toLowerCase().includes('aspire')) return <RocketLaunchIcon sx={{ fontSize: '6rem' }} />;
                  return <TrophyIcon sx={{ fontSize: '6rem' }} />;
                };
                
                return (
                  <Box key={league.id} sx={{ flex: 1, minWidth: 0 }}>
                    <Card 
                      variant={isSelected ? "elevation" : "outlined"}
                      sx={{ 
                        cursor: 'pointer',
                        border: isSelected ? 2 : 1,
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        backgroundColor: isSelected ? 'primary.main' : 'background.paper',
                        color: isSelected ? 'primary.contrastText' : 'inherit',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: isSelected ? 'primary.main' : 'grey.50'
                        }
                      }}
                      onClick={() => setSelectedLeague(league.id)}
                    >
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          mb: 2 
                        }}>
                          <Box sx={{ 
                            color: isSelected ? 'primary.contrastText' : 'primary.main',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            {getLeagueIcon(league.name)}
                          </Box>
                        </Box>

                        <Typography 
                          variant="h6" 
                          component="h3"
                          sx={{ 
                            color: 'inherit',
                            fontWeight: 'medium',
                            mb: 2
                          }}
                        >
                          {league.name}
                        </Typography>

                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'inherit',
                            mb: 2,
                            minHeight: '3em'
                          }}
                        >
                          {league.description || 'No description available'}
                        </Typography>

                      </CardContent>
                    </Card>
                  </Box>
                );
              });
            })()}
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={setSeekingTeam}
                onChange={(e) => setSetSeekingTeam(e.target.checked)}
                color="primary"
              />
            }
            label="Also mark student as 'Seeking Team'"
            sx={{ mb: 2 }}
          />

          {availableFtcLeagues.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No FTC leagues are currently available for enrollment.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFtcLeagueDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleFtcLeagueEnrollment}
            variant="contained"
            disabled={!selectedStudent || !selectedLeague || getAvailableStudentsForLeagueEnrollment().length === 0}
          >
            Enroll Student
          </Button>
        </DialogActions>
      </Dialog>

      {/* Availability Edit Dialog */}
      <Dialog open={availabilityDialogOpen} onClose={() => setAvailabilityDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ScheduleIcon />
            Edit Student Availability
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a student from your household to edit their availability.
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Select Student</InputLabel>
            <Select
              value={selectedStudentForAvailability}
              onChange={(e) => setSelectedStudentForAvailability(e.target.value)}
              label="Select Student"
            >
              {students.map((student) => (
                <MenuItem key={student.id} value={student.id}>
                  {formatUserName(student)}
                  {student.school && ` - ${student.school}`}
                  {student.graduationYear && ` (Class of ${student.graduationYear})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedStudentForAvailability && (
            <AvailabilityEditForm 
              studentId={selectedStudentForAvailability}
              studentName={students.find(s => s.id === selectedStudentForAvailability)?.preferredName || students.find(s => s.id === selectedStudentForAvailability)?.firstName}
              onSave={() => {
                setAvailabilityDialogOpen(false);
                setSelectedStudentForAvailability('');
                loadHouseholdData(); // Refresh data
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvailabilityDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Caregiver Availability Dialog */}
      <Dialog 
        open={caregiverAvailabilityDialogOpen} 
        onClose={() => {
          trackUIInteraction('caregiver_availability_dialog_closed', () => {
            // This is the callback that will be executed within the Sentry span
            setCaregiverAvailabilityDialogOpen(false);
          }, {
            householdId: householdId,
            caregiverId: selectedCaregiverForAvailability
          });
        }} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ScheduleIcon />
            Modify Caregiver Availability
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a caregiver from your household to modify their availability for team supervision.
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Note:</strong> Availabilities are automatically saved as you make changes, so no need to worry about losing your selections!
          </Alert>
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Select Caregiver</InputLabel>
            <Select
              value={selectedCaregiverForAvailability}
              onChange={(e) => setSelectedCaregiverForAvailability(e.target.value)}
              label="Select Caregiver"
            >
              {caregivers.map((caregiver) => (
                <MenuItem key={caregiver.id} value={caregiver.id}>
                  {formatUserName(caregiver)}
                  {primaryCaregiver && primaryCaregiver.id === caregiver.id && ' (Primary)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedCaregiverForAvailability && (
            <TeamSupervisorAvailabilityForm 
              householdId={householdId}
              students={students}
              onAvailabilitiesChange={handleCaregiverAvailabilityChange}
              userId={selectedCaregiverForAvailability}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCaregiverAvailabilityDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HouseholdDashboard; 